import DataGrid, { Column, Editing, Export, FilterRow, GroupPanel, Grouping, Paging, SearchPanel } from 'devextreme-react/data-grid';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Badge, Button, Card, CardBody, Col, Input, Label, Nav, NavItem, NavLink, Row } from 'reactstrap';
import Breadcrumb from '../../components/common/breadcrumb';
import { onExporting } from '../../utils/DataGridExporter';
import {
    getCustodyReport, getDeliveries, getMasterData, getMyCustodyPermissions, getOpenDamages, getOpenStarts, saveCustodyCode,
    updateDamageStatus,
} from './api';
import AkisTanimlari, { GateEvents } from './AkisTanimlari';
import { PodDoc } from '../sase-sorgulama/components/DevirTeslim';
import './devir-teslim.css';

// Control-tower view of custody and damage: open damages (with status workflow), handovers not completed by the
// receiving party, damage reports / KPIs, and code-list maintenance.

const LATE_HOURS = 12; // RUA: inspection of a night / out-of-hours delivery by 12:00 next working day

const StatusEditor = ({ damage, statuses, perms, onSaved, onCancel }) => {
    const [status, setStatus] = useState(damage.status);
    const [responsible, setResponsible] = useState(damage.responsible_party || '');
    const [action, setAction] = useState('');
    const [note, setNote] = useState('');
    const allowed = statuses.filter((s) => s.is_active && ((s.is_closed || !s.blocks_shipment) ? perms.hasar_kapatma_write : perms.hasar_degerlendirme_write));
    const target = statuses.find((s) => s.code === status);
    const save = async () => {
        try {
            await updateDamageStatus({ line_id: damage.id, status, responsible_party: responsible, action_text: action, note });
            toast.success('Hasar güncellendi');
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Kaydedilemedi');
        }
    };
    return (
        <div className="dt-draft mt-2">
            <Row>
                <Col md="3"><Label>Statü</Label>
                    <Input type="select" value={status} onChange={(e) => setStatus(e.target.value)}>
                        {statuses.filter((s) => s.code === damage.status || allowed.includes(s)).map((s) =>
                            <option key={s.code} value={s.code}>{s.name_tr}{s.blocks_shipment && !s.is_closed ? ' (sevki engeller)' : ''}</option>)}
                    </Input></Col>
                <Col md="3"><Label>Sorumlu taraf</Label><Input value={responsible} onChange={(e) => setResponsible(e.target.value)} /></Col>
                <Col md="3"><Label>Aksiyon</Label><Input value={action} placeholder="ör. onarım, tazmin talebi" onChange={(e) => setAction(e.target.value)} /></Col>
                <Col md="3"><Label>Açıklama{target?.needs_note ? ' (zorunlu)' : ''}</Label><Input value={note} onChange={(e) => setNote(e.target.value)} /></Col>
            </Row>
            <div className="mt-2">
                <Button size="sm" color="primary" className="mr-2" onClick={save}>Kaydet</Button>
                <Button size="sm" color="secondary" onClick={onCancel}>Vazgeç</Button>
            </div>
        </div>
    );
};

const History = ({ items }) => (
    <div className="ci-history">
        {items.map((h, i) => (
            <div key={i}>{moment(h.changed_at).format('DD.MM.YYYY HH:mm')} · {h.changed_by_name}: {h.from_status} → <b>{h.to_status_name}</b>
                {h.to_responsible !== h.from_responsible && ` · sorumlu: ${h.from_responsible || '—'} → ${h.to_responsible || '—'}`}
                {h.action_text && ` · aksiyon: ${h.action_text}`}{h.note && ` · ${h.note}`}</div>
        ))}
    </div>
);

const KpiTable = ({ title, rows }) => {
    const max = Math.max(1, ...rows.map((r) => r.damage_rate_pct));
    return (
        <Card><CardBody>
            <h6>{title}</h6>
            <table className="ci-kpi">
                <thead><tr><th></th><th>Devir-teslim</th><th>Hasarlı araç</th><th>Yeni hasar</th><th>Hasar oranı</th></tr></thead>
                <tbody>{rows.map((r) => (
                    <tr key={r.key}><td>{r.key}</td><td>{r.handovers}</td><td>{r.damaged_vehicles}</td><td>{r.new_damages}</td>
                        <td><span className="ci-bar" style={{ width: `${(r.damage_rate_pct / max) * 80}px` }} />%{r.damage_rate_pct}</td></tr>
                ))}</tbody>
            </table>
        </CardBody></Card>
    );
};

const CODE_TABLES = {
    rua_component: { label: 'RUA parçaları', key: 'components', cols: [['name_tr', 'Ad (TR)'], ['name_en', 'Ad (EN)'], ['is_active', 'Aktif', 'boolean']] },
    rua_damage_code: { label: 'RUA hasar kodları', key: 'damage_codes', cols: [['name_tr', 'Ad (TR)'], ['name_en', 'Ad (EN)'], ['threshold', 'RUA ölçütü'],
        ['blocks_shipment', 'Sevki engeller', 'boolean'], ['is_active', 'Aktif', 'boolean']] },
    damage_status: { label: 'Hasar statüleri', key: 'statuses', cols: [['name_tr', 'Ad (TR)'], ['name_en', 'Ad (EN)'], ['blocks_shipment', 'Sevki engeller', 'boolean'],
        ['needs_note', 'Açıklama zorunlu', 'boolean'], ['is_active', 'Aktif', 'boolean']] },
    rua_observation: { label: 'Gözlem / çekince listesi', key: 'observations', cols: [['line_type', 'Tip (GOZLEM/CEKINCE)'], ['name_tr', 'Ad (TR)'], ['name_en', 'Ad (EN)']] },
    refusal_reason: { label: 'Teslim edilememe / iade nedenleri', key: 'refusal_reasons', cols: [['name_tr', 'Ad (TR)'], ['name_en', 'Ad (EN)'], ['is_active', 'Aktif', 'boolean']] },
};

// Final deliveries (proof of delivery): one row per dealer / special-address delivery, planned vs actual place,
// carrier and receiver, result, the signed delivery note, and whether the delivery date was written by the POD.
const Deliveries = () => {
    const [from, setFrom] = useState(moment().subtract(30, 'days').format('YYYY-MM-DD'));
    const [to, setTo] = useState(moment().format('YYYY-MM-DD'));
    const [rows, setRows] = useState(null);
    const load = useCallback(() => {
        getDeliveries(from, to).then(setRows).catch((e) => { setRows([]); toast.error(e?.response?.data?.error || 'Teslimatlar alınamadı'); });
    }, [from, to]);
    useEffect(() => { load(); }, [load]);
    const list = rows || [];
    const off = list.filter((d) => !d.matches_plan).length;
    const damaged = list.filter((d) => d.result === 'HASARLI').length;
    const noDoc = list.filter((d) => d.documents.length === 0).length;
    return (
        <Card><CardBody>
            <div className="d-flex align-items-end mb-2" style={{ gap: 8 }}>
                <div><Label className="mb-0">Başlangıç</Label><Input bsSize="sm" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
                <div><Label className="mb-0">Bitiş</Label><Input bsSize="sm" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
                <div className="dt-muted ml-2">{list.length} teslimat · {damaged} hasarlı · {off} planlanandan farklı yerde · {noDoc} belgesiz</div>
            </div>
            <div className="dt-muted mb-2">Bayi / özel adres teslimleri: el terminalinde iki tarafın imzaladığı son devir-teslim. Teslim tarihi bu kayıttan yazılır
                (daha önce girilmişse korunur). Planlanan = sevk emrindeki bayi, gerçekleşen = teslim edilen lokasyon.</div>
            <table className="ct-table">
                <thead><tr><th>Teslim</th><th>VIN</th><th>Planlanan → gerçekleşen</th><th>Taşıyıcı</th><th>Teslim alan</th><th>Sonuç</th><th>Teslim tarihi</th><th>Belge</th></tr></thead>
                <tbody>{list.map((d) => (
                    <tr key={d.id}>
                        <td>{moment(d.delivered_at).format('DD.MM.YYYY HH:mm')}<div className="dt-muted">{d.handover_no}</div></td>
                        <td><b>{d.chassis_no}</b><div className="dt-muted">{d.brand} {d.model}</div></td>
                        <td>{d.matches_plan ? <><Badge color="success">plana uygun</Badge> {d.actual_location}</>
                            : <><Badge color="warning">farklı</Badge> {d.planned_location || 'emirde varış yok'} → <b>{d.actual_location || '—'}</b></>}
                            <div className="dt-muted">{d.actual_place}{d.order_ref ? ` · ${d.order_ref}` : ''}</div></td>
                        <td>{d.carrier}<div className="dt-muted">{d.delivering_person}{d.transport_ref ? ` · ${d.transport_ref}` : ''}{d.groupage_no ? ` · ${d.groupage_no}` : ''}</div></td>
                        <td>{d.receiving_party}<div className="dt-muted">{d.receiving_person}</div></td>
                        <td><Badge color={d.result === 'HASARLI' ? 'danger' : 'success'}>{d.result === 'HASARLI' ? `HASARLI (${d.damage_count})` : 'HASARSIZ'}</Badge></td>
                        <td>{d.earlier_delivery_date ? <>{d.earlier_delivery_date}<div className="dt-muted">önceden girilmiş, korundu</div></>
                            : <>{moment(d.delivered_at).format('DD.MM.YYYY HH:mm')}<div className="dt-muted">POD ile yazıldı</div></>}</td>
                        <td className="ct-photos">{d.documents.length ? d.documents.map((p) => <PodDoc key={p} path={p} />) : <Badge color="secondary">yok</Badge>}</td>
                    </tr>
                ))}</tbody>
            </table>
            {rows && list.length === 0 && <div className="dt-muted p-2">Bu aralıkta teslimat yok.</div>}
        </CardBody></Card>
    );
};

const CodeLists = ({ md, reload }) => {
    const [table, setTable] = useState('damage_status');
    const def = CODE_TABLES[table];
    const persist = (code, values) => saveCustodyCode(table, code, values)
        .then(() => { toast.success('Kaydedildi'); reload(); return false; })
        .catch((e) => { toast.error(e?.response?.data?.error || 'Kaydedilemedi'); return true; });
    return (
        <Card><CardBody>
            <div className="d-flex align-items-center mb-2">
                <Label className="mr-2 mb-0">Liste</Label>
                <Input type="select" style={{ maxWidth: 320 }} value={table} onChange={(e) => setTable(e.target.value)}>
                    {Object.entries(CODE_TABLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </Input>
                <span className="dt-muted ml-3">Değişiklikler kod geliştirmeden geçerli olur ve kim/ne zaman bilgisiyle loglanır.
                    Blokaj tipleri: Tanımlamalar &gt; Blokaj Tipleri.</span>
            </div>
            <DataGrid dataSource={md[def.key]} keyExpr="code" showBorders columnAutoWidth wordWrapEnabled
                onRowUpdating={(e) => { e.cancel = persist(e.key, e.newData); }}
                onRowInserting={(e) => { const { code, ...values } = e.data; e.cancel = persist(code, values); }}>
                <Editing mode="row" allowUpdating allowAdding={table !== 'rua_zone'} />
                <Paging pageSize={20} />
                <SearchPanel visible />
                <Column dataField="code" caption="Kod" allowEditing={false} width={150}
                    editCellRender={(c) => (c.row.isNewRow ? <Input onChange={(e) => c.setValue(e.target.value)} /> : c.value)} />
                {def.cols.map(([f, cap, type]) => <Column key={f} dataField={f} caption={cap} dataType={type || 'string'} />)}
            </DataGrid>
        </CardBody></Card>
    );
};

const DevirTeslimIzleme = () => {
    const [tab, setTab] = useState('damages');
    const [perms, setPerms] = useState({});
    const [md, setMd] = useState(null);
    const [damages, setDamages] = useState([]);
    const [starts, setStarts] = useState([]);
    const [editing, setEditing] = useState(null);
    const [range, setRange] = useState({ from: moment().subtract(30, 'days').format('YYYY-MM-DD'), to: moment().format('YYYY-MM-DD') });
    const [report, setReport] = useState(null);

    const load = useCallback(() => {
        getMasterData().then(setMd).catch(() => {});
        getOpenDamages().then(setDamages).catch(() => toast.error('Açık hasarlar alınamadı'));
        getOpenStarts().then(setStarts).catch(() => {});
    }, []);
    const loadReport = useCallback(() => {
        getCustodyReport(range.from, range.to).then(setReport).catch((e) => toast.error(e?.response?.data?.error || 'Rapor alınamadı'));
    }, [range]);

    useEffect(() => { getMyCustodyPermissions().then(setPerms).catch(() => {}); load(); }, [load]);
    useEffect(() => { if (tab === 'reports' && !report) loadReport(); }, [tab, report, loadReport]);

    const canEvaluate = perms.hasar_degerlendirme_write || perms.hasar_kapatma_write;
    const blocking = damages.filter((d) => d.blocks_shipment).length;
    const late = starts.filter((s) => s.age_hours > LATE_HOURS).length;

    return (
        <div>
            <Breadcrumb parent="Operasyon İzleme" title="Devir-Teslim ve Hasar İzleme" />
            <Row className="mb-2">
                {[['Açık hasar', damages.length, 'danger'], ['Sevki engelleyen', blocking, 'danger'],
                    ['Tamamlanmamış devir-teslim', starts.length, 'warning'], [`${LATE_HOURS} saati geçen`, late, 'warning']].map(([t, n, c]) => (
                    <Col md="3" key={t}><Card><CardBody className="py-3">
                        <div className="dt-muted">{t}</div><h3 className={`mb-0 text-${n ? c : 'success'}`}>{n}</h3>
                    </CardBody></Card></Col>
                ))}
            </Row>
            <Nav tabs className="border-tab nav-primary ci-tabs">
                {[['damages', `Açık hasarlar (${damages.length})`], ['starts', `Tamamlanmamış devir-teslimler (${starts.length})`], ['gates', 'Kapı kontrolleri'],
                    ['deliveries', 'Teslimatlar (POD)'], ['reports', 'Raporlar'], ['flow', 'Akış tanımları'],
                    ...(perms.custody_kod_yonetimi_write ? [['codes', 'Kod listeleri']] : [])].map(([k, t]) => (
                    <NavItem key={k}><NavLink className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>{t}</NavLink></NavItem>
                ))}
            </Nav>

            {tab === 'damages' && (
                <Card><CardBody>
                    <table className="ct-table">
                        <thead><tr><th>VIN</th><th>Hasar</th><th>Tespit</th><th>Gün</th><th>Sorumlu</th><th>Statü</th><th></th></tr></thead>
                        <tbody>{damages.map((d) => (
                            <React.Fragment key={d.id}>
                                <tr>
                                    <td><b>{d.chassis_no}</b><div className="dt-muted">{d.brand} {d.model}</div></td>
                                    <td><b>{d.component_code}</b> {d.component_name} · <b>{d.damage_code}</b> {d.damage_name}<div className="dt-muted">{d.zone_name}{d.size_text ? ` · ${d.size_text}` : ''}</div></td>
                                    <td>{d.handover_no}<div className="dt-muted">{d.point_name} · {d.location_name} · {moment(d.handover_at).format('DD.MM.YYYY HH:mm')}</div></td>
                                    <td>{d.age_days.toFixed(1)}</td>
                                    <td>{d.responsible_party}</td>
                                    <td>{d.status_name}{d.blocks_shipment && <div><Badge color="danger">Sevki engeller</Badge></div>}</td>
                                    <td>{canEvaluate && <Button size="sm" color="primary" outline onClick={() => setEditing(editing === d.id ? null : d.id)}>Değerlendir</Button>}</td>
                                </tr>
                                {(editing === d.id || d.history.length > 0) && (
                                    <tr><td colSpan="7">
                                        {d.history.length > 0 && <History items={d.history} />}
                                        {editing === d.id && md && <StatusEditor damage={d} statuses={md.statuses} perms={perms}
                                            onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}
                                    </td></tr>
                                )}
                            </React.Fragment>
                        ))}</tbody>
                    </table>
                    {damages.length === 0 && <div className="dt-muted p-2">Açık hasar yok.</div>}
                </CardBody></Card>
            )}

            {tab === 'starts' && (
                <Card><CardBody>
                    <div className="dt-muted mb-2">Teslim eden tarafın imzaladığı, teslim alan tarafın henüz kontrol edip onaylamadığı devir-teslimler.</div>
                    <table className="ct-table">
                        <thead><tr><th>No</th><th>VIN</th><th>Nokta / lokasyon</th><th>Teslim eden</th><th>Teslim alacak</th><th>Teslim zamanı</th><th>Bekleme</th><th>Not</th></tr></thead>
                        <tbody>{starts.map((s) => (
                            <tr key={s.id}>
                                <td>{s.start_no}</td><td><b>{s.chassis_no}</b><div className="dt-muted">{s.brand} {s.model}</div></td>
                                <td>{s.point_name}<div className="dt-muted">{s.location_name}</div></td>
                                <td>{s.delivering_party}<div className="dt-muted">{s.delivering_person}</div></td>
                                <td>{s.receiving_party}</td>
                                <td>{moment(s.started_at).format('DD.MM.YYYY HH:mm')}<div className="dt-muted">kaydeden {s.started_by_name}</div></td>
                                <td className={s.age_hours > LATE_HOURS ? 'ci-age-late' : ''}>{s.age_hours.toFixed(1)} saat</td>
                                <td>{s.note}</td>
                            </tr>
                        ))}</tbody>
                    </table>
                    {starts.length === 0 && <div className="dt-muted p-2">Tamamlanmamış devir-teslim yok.</div>}
                </CardBody></Card>
            )}

            {tab === 'reports' && (
                <div>
                    <Card><CardBody className="d-flex align-items-end">
                        <div className="mr-2"><Label>Başlangıç</Label><Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} /></div>
                        <div className="mr-2"><Label>Bitiş</Label><Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} /></div>
                        <Button color="primary" onClick={loadReport}>Raporla</Button>
                    </CardBody></Card>
                    {report && (
                        <>
                            <Row>
                                <Col lg="4"><KpiTable title="Teslim eden taraf bazında (taşıyıcı, gemi, liman…)" rows={report.by_party} /></Col>
                                <Col lg="4"><KpiTable title="Devir-teslim noktası bazında" rows={report.by_point} /></Col>
                                <Col lg="4"><KpiTable title="Lokasyon bazında" rows={report.by_location} /></Col>
                            </Row>
                            <Card><CardBody>
                                <h6>Hasar detayı ({report.damages.length})</h6>
                                <DataGrid dataSource={report.damages} keyExpr="line_id" showBorders columnAutoWidth onExporting={onExporting}>
                                    <FilterRow visible /><GroupPanel visible /><Grouping autoExpandAll /><Paging pageSize={25} />
                                    <Export enabled />
                                    <Column dataField="handover_at" caption="Tarih" dataType="datetime" format="dd.MM.yyyy HH:mm" />
                                    <Column dataField="handover_no" caption="Tutanak" />
                                    <Column dataField="chassis_no" caption="VIN" />
                                    <Column dataField="brand" caption="Marka" />
                                    <Column dataField="model" caption="Model" />
                                    <Column dataField="point_name" caption="Devir-teslim noktası" />
                                    <Column dataField="location_name" caption="Lokasyon" />
                                    <Column dataField="delivering_party" caption="Teslim eden (taşıyıcı)" />
                                    <Column dataField="transport_mode" caption="Taşıma" />
                                    <Column dataField="transport_ref" caption="Gemi / plaka" />
                                    <Column dataField="zone_name" caption="Bölge" />
                                    <Column dataField="component_name" caption="Parça" calculateCellValue={(r) => `${r.component_code} ${r.component_name}`} />
                                    <Column dataField="damage_name" caption="Hasar tipi" calculateCellValue={(r) => `${r.damage_code} ${r.damage_name}`} />
                                    <Column dataField="status_name" caption="Statü" />
                                    <Column dataField="responsible_party" caption="Sorumlu" />
                                </DataGrid>
                            </CardBody></Card>
                        </>
                    )}
                </div>
            )}

            {tab === 'codes' && md && <CodeLists md={md} reload={load} />}
            {tab === 'gates' && <GateEvents />}
            {tab === 'deliveries' && <Deliveries />}
            {tab === 'flow' && md && <AkisTanimlari md={md} canEdit={!!perms.custody_kod_yonetimi_write} reload={load} />}
        </div>
    );
};

export default DevirTeslimIzleme;
